/**
 * Renders the Client Care Letter body written in a lightweight markup:
 *   # Heading        →  navy bold heading
 *   ## Sub-heading   →  smaller navy bold heading
 *   **bold**         →  bold inline
 *   - bullet / * b   →  bulleted list
 *   blank line       →  paragraph break
 *
 * Renders as safe React elements (no dangerouslySetInnerHTML). Kept in
 * sync with the backend reportlab renderer (`_body_flowables` in
 * routes/onboarding_ccl.py) so the on-screen preview matches the signed PDF.
 */
function renderInline(text, keyPrefix) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.filter((p) => p !== "").map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <strong key={`${keyPrefix}-b${i}`}>{p.slice(2, -2)}</strong>;
    }
    return <span key={`${keyPrefix}-t${i}`}>{p}</span>;
  });
}

export default function CclBody({ text = "", className = "" }) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let para = [];
  let bullets = [];
  const flushPara = () => { if (para.length) { blocks.push({ type: "p", lines: [...para] }); para = []; } };
  const flushBul = () => { if (bullets.length) { blocks.push({ type: "ul", items: [...bullets] }); bullets = []; } };

  for (const raw of lines) {
    const s = raw.trim();
    const hMatch = s.match(/^(#{1,3})\s+(.*)$/);
    if (hMatch) { flushPara(); flushBul(); blocks.push({ type: "h", level: hMatch[1].length, text: hMatch[2] }); continue; }
    if (/^[-*]\s+/.test(s)) { flushPara(); bullets.push(s.replace(/^[-*]\s+/, "")); continue; }
    if (s === "") { flushPara(); flushBul(); continue; }
    flushBul();
    para.push(raw);
  }
  flushPara();
  flushBul();

  return (
    <div className={className}>
      {blocks.map((b, bi) => {
        if (b.type === "h") {
          return (
            <p key={bi}
              className={`font-bold text-[#100f5d] ${b.level === 1 ? "text-[15px] mt-4 mb-1.5" : "text-[13px] mt-3 mb-1"}`}>
              {renderInline(b.text, `h${bi}`)}
            </p>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={bi} className="list-disc pl-5 my-2 space-y-1">
              {b.items.map((it, ii) => <li key={ii}>{renderInline(it, `u${bi}-${ii}`)}</li>)}
            </ul>
          );
        }
        return (
          <p key={bi} className="mb-2.5">
            {b.lines.map((l, li) => (
              <span key={li}>
                {renderInline(l, `p${bi}-${li}`)}
                {li < b.lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}
